import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Coffee, LayoutDashboard, Menu, Search, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { BUY_ME_A_COFFEE_URL } from "../lib/brand";
import BrandLogo from "./BrandLogo";
import SiteActionBar from "./SiteActionBar";
import { api, trackNavClick } from "../lib/api";
import { lockBodyScroll, unlockBodyScroll } from "../lib/scrollLock";

const LOGO_SIZE_CLASS = {
  sm: "h-14 sm:h-10",
  md: "h-16 sm:h-14",
  lg: "h-20 sm:h-20",
  xl: "h-24 sm:h-24",
  "2xl": "h-28 sm:h-32",
  "3xl": "h-32 sm:h-40",
};

// Mobile-only full-width banner row (below `sm`, 640px). Reduced from
// the original h-20..h-40 scale per user request — those rendered far
// too tall (e.g. "lg" = h-28/112px) once the header padding was
// tightened; this scale tops out at h-28 instead.
const LOGO_SIZE_CLASS_MOBILE_LG = {
  sm: "h-10",
  md: "h-14",
  lg: "h-16",
  xl: "h-20",
  "2xl": "h-24",
  "3xl": "h-28",
};

const BRAND_TEXT_SIZE_CLASS = {
  sm: "text-xl sm:text-lg",
  md: "text-2xl sm:text-xl",
  lg: "text-3xl sm:text-2xl",
  xl: "text-4xl sm:text-3xl",
  "2xl": "text-5xl sm:text-4xl",
  "3xl": "text-6xl sm:text-5xl",
};

// Pairs with LOGO_SIZE_CLASS_MOBILE_LG above for the mobile-only banner row.
const BRAND_TEXT_SIZE_CLASS_MOBILE_LG = {
  sm: "text-2xl",
  md: "text-3xl",
  lg: "text-4xl",
  xl: "text-5xl",
  "2xl": "text-6xl",
  "3xl": "text-7xl",
};

const BRAND_FONT_CLASS = {
  display: "font-display",   // Outfit
  sans: "font-sans",
  serif: "font-serif",
  mono: "font-mono",
};

const useNavItems = () => {
  // Single source of truth — `/api/nav-items` now returns both nav_items
  // and custom-pages flagged show_in_nav, merged + sorted by position.
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    api
      .get("/nav-items")
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setItems(
          list.map((it) => ({
            id: it.id,
            label: it.label,
            to: it.to === "/stats" ? "/football-stats" : it.to,
            target: it.target || "self",
            parent_id: it.parent_id || "",
          })),
        );
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return items;
};

// Group flat nav items into `{ tops: [...], childrenOf: {parentId: [...]} }`.
// Only 1 level deep is supported — a child cannot also be a parent
// (enforced server-side in /admin/nav-items PUT). Items whose parent is
// missing (deleted / disabled) fall back to top-level so the menu never
// silently drops entries.
const buildNavTree = (flat) => {
  const idSet = new Set(flat.map((it) => it.id));
  const tops = [];
  const childrenOf = {};
  flat.forEach((it) => {
    const pid = it.parent_id;
    if (pid && idSet.has(pid)) {
      if (!childrenOf[pid]) childrenOf[pid] = [];
      childrenOf[pid].push(it);
    } else {
      tops.push(it);
    }
  });
  return { tops, childrenOf };
};

const getInternalPath = (toUrl) => {
  if (!toUrl) return "";
  if (!/^https?:\/\//i.test(toUrl)) return toUrl;
  try {
    const urlObj = new URL(toUrl);
    const winHost = typeof window !== "undefined" ? window.location.host : "";
    if (urlObj.host === winHost) {
      return urlObj.pathname + urlObj.search + urlObj.hash;
    }
  } catch (_) {
    // ignore
  }
  return null;
};


export const Header = ({ hideActionBar = false, mobileActionBarOnly = false, hideLogo = false, hideMobileChrome = false } = {}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const fullNav = useNavItems();

  // Dynamically measure the header height so the mobile drawer can
  // cap its own max-height to `viewport - header`. Static values are
  // brittle now that the action bar sits inside the header — its
  // visibility and the desktop nav row both change the header's
  // natural height.
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(73);
  useEffect(() => {
    if (!headerRef.current) return undefined;
    const el = headerRef.current;
    const update = () => setHeaderH(el.offsetHeight || 73);
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (menuOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    return () => {
      if (menuOpen) {
        unlockBodyScroll();
      }
    };
  }, [menuOpen]);

  // Header bg: explicit theme colour if set, else transparent (inherits page bg).
  // We keep the frosted-glass backdrop-blur so the header still feels layered
  // when the user picks a non-white page background.
  const headerBg = (theme.header_bg_color || "").trim();

  // Mobile drawer background: combine the admin-picked hex with the
  // 0-100 opacity slider. Falls back to opaque white so older settings
  // documents keep working.
  const drawerHex = ((theme.mobile_menu_bg_color || "").trim() || "#FFFFFF");
  const drawerOpacity = Math.max(0, Math.min(100, Number(theme.mobile_menu_opacity ?? 100))) / 100;
  const _hexToRgba = (hex, a) => {
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };
  const drawerBg = _hexToRgba(drawerHex, drawerOpacity);
  const drawerBgImage = (theme.mobile_menu_bg_image || "").trim();
  const headerStyle = headerBg
    ? { backgroundColor: `${headerBg}BF` } // BF = ~75% alpha, matches original white/75 vibe
    : { backgroundColor: "transparent" };

  // Suppress the public action bar on admin routes — admins already
  // have their own AdminHealthBadge / utility rail and don't need
  // "Get alerts / Install LAST" surfaced on every CMS page.
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Hamburger trigger for true-mobile (<640px) — sits in the top brand
  // row next to the logo (mirrors the 640–767px sliver copy below and
  // the desktop nav's hamburger absence at ≥768px).
  const mobileHamburgerButton = (
    <button
      type="button"
      aria-label={menuOpen ? "Close menu" : "Open menu"}
      aria-expanded={menuOpen}
      onClick={() => setMenuOpen((v) => !v)}
      data-testid="mobile-menu-toggle"
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:hidden"
    >
      {menuOpen ? <X size={18} /> : <Menu size={18} />}
    </button>
  );

  return (
    <>
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 border-b border-black/10 backdrop-blur-xl ${hideMobileChrome ? "max-md:hidden" : ""}`}
      style={headerStyle}
      data-testid="site-header"
    >
      <div
        className={`mx-auto max-w-7xl px-5 sm:px-8 py-1 sm:py-4 ${hideMobileChrome ? "max-md:hidden" : ""}`}
      >
        {/* Mobile-only (<640px): logo bleeds full-width edge-to-edge (the
            `-mx-5` cancels the parent's `px-5`), hamburger stays at its
            previous position but is now an absolutely-positioned overlay
            on top of it, so the logo extends behind the button. Fixed
            full size at all times — no shrink/scroll animation, per user
            request (a previous condense-on-scroll effect was removed
            entirely; only the ticker above scrolls away with the page
            now, and that's just normal document flow, not JS-driven). */}
        {hideLogo ? null : (
          <div className="relative -mx-5 sm:hidden">
          <Link
            to="/"
            className="flex w-full items-center gap-3"
            data-testid="header-logo-mobile"
          >
            <BrandLogo
              src={theme.brand_logo_url_mobile || theme.brand_logo_url}
              alt={theme.brand_name || "LAST"}
              className={LOGO_SIZE_CLASS_MOBILE_LG[theme.brand_logo_size] || LOGO_SIZE_CLASS_MOBILE_LG.md}
            />
            {theme.brand_show_name !== false ? (
              <span
                className={`${
                  BRAND_FONT_CLASS[theme.brand_font] || BRAND_FONT_CLASS.display
                } ${
                  BRAND_TEXT_SIZE_CLASS_MOBILE_LG[theme.brand_logo_size] ||
                  BRAND_TEXT_SIZE_CLASS_MOBILE_LG.md
                } truncate font-black leading-none tracking-tight text-[var(--brand-ink)]`}
                style={
                  theme.brand_name_color ? { color: theme.brand_name_color } : undefined
                }
              >
                {theme.brand_name || "LAST"}
              </span>
            ) : null}
          </Link>
          <div className="absolute right-5 top-1/2 z-10 -translate-y-1/2">
            {mobileHamburgerButton}
          </div>
          </div>
        )}

        {/* Tablet/desktop (≥640px): unchanged original layout — logo left,
            nav centered, admin link + hamburger (640–767px sliver only,
            since nav itself only shows from 768px up) on the right.
            Optional header banner: a full-width image bleeding past this
            row's own left/right padding to the true viewport edges,
            replacing the logo entirely — nav/admin/hamburger stay in
            their normal (padded) spot on top via z-10, unaffected by the
            bleed since only the <img> itself carries the negative offset. */}
        <div
          className={`relative hidden items-center sm:flex ${hideLogo ? "justify-center" : "justify-between"}`}
        >
        {theme.brand_header_banner_url ? (
          <img
            src={theme.brand_header_banner_url}
            alt=""
            aria-hidden="true"
            className="absolute inset-y-0 left-[-20px] right-[-20px] z-0 h-full w-[calc(100%+40px)] object-cover sm:left-[-32px] sm:right-[-32px] sm:w-[calc(100%+64px)]"
            data-testid="header-banner-img"
          />
        ) : null}
        {hideLogo || theme.brand_header_banner_url ? null : (
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="header-logo"
        >
          <BrandLogo
            src={theme.brand_logo_url}
            alt={theme.brand_name || "LAST"}
            className={LOGO_SIZE_CLASS[theme.brand_logo_size] || LOGO_SIZE_CLASS.md}
          />
          {theme.brand_show_name !== false || theme.brand_show_tagline !== false ? (
            <div className="flex flex-col leading-tight">
              {theme.brand_show_name !== false ? (
                <span
                  className={`${
                    BRAND_FONT_CLASS[theme.brand_font] || BRAND_FONT_CLASS.display
                  } ${
                    BRAND_TEXT_SIZE_CLASS[theme.brand_logo_size] || BRAND_TEXT_SIZE_CLASS.md
                  } font-black tracking-tight text-[var(--brand-ink)]`}
                  style={
                    theme.brand_name_color ? { color: theme.brand_name_color } : undefined
                  }
                >
                  {theme.brand_name || "LAST"}
                </span>
              ) : null}
              {theme.brand_show_tagline !== false && theme.brand_tagline ? (
                <span
                  className="label-kicker hidden text-[var(--brand-body)] sm:block"
                  style={
                    theme.brand_tagline_color
                      ? { color: theme.brand_tagline_color }
                      : undefined
                  }
                >
                  {theme.brand_tagline}
                </span>
              ) : null}
            </div>
          ) : null}
        </Link>
        )}

        {/* Desktop nav */}
        <nav
          className="relative z-10 hidden items-center gap-1 rounded-full border border-black/10 bg-white p-1 md:flex"
          data-testid="header-nav"
        >
          {(() => {
            const { tops, childrenOf } = buildNavTree(fullNav);
            return tops.map((t) => {
              const kids = childrenOf[t.id] || [];
              if (kids.length > 0) {
                return <DesktopNavDropdown key={t.id} item={t} kids={kids} />;
              }
              const internalPath = getInternalPath(t.to);
              const isExternal =
                t.target === "blank" || (internalPath === null && /^https?:\/\//i.test(t.to || ""));
              const cls =
                "relative whitespace-nowrap rounded-full px-3 py-1.5 font-display text-sm font-semibold transition-colors text-[#002d75] hover:bg-[#002d75]/10";
              if (isExternal) {
                return (
                  <a
                    key={t.id}
                    href={t.to}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackNavClick(t.id)}
                    data-testid={`nav-tab-${t.id}`}
                    className={cls}
                  >
                    {t.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={t.id}
                  to={internalPath || t.to}
                  end={t.to === "/"}
                  onClick={() => trackNavClick(t.id)}
                  data-testid={`nav-tab-${t.id}`}
                  className={({ isActive }) =>
                    `relative whitespace-nowrap rounded-full px-3 py-1.5 font-display text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-[#002d75] text-[#6CABDD]"
                        : "text-[#002d75] hover:bg-[#002d75]/10"
                    }`
                  }
                >
                  {t.label}
                </NavLink>
              );
            });
          })()}
        </nav>

        <div className={`relative z-10 flex items-center gap-2 ${hideMobileChrome ? "max-md:hidden" : ""}`}>
          {user?.role === "admin" ? (
            <Link
              to="/admin"
              data-testid="header-admin-link"
              className="hidden items-center gap-1.5 rounded-md border border-[#0A0F17] px-3 py-2 font-display text-sm font-bold text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:inline-flex"
            >
              <LayoutDashboard size={14} strokeWidth={2.5} /> Admin
            </Link>
          ) : null}

          {/* Hamburger — visible only in the 640–767px sliver here (nav
              itself doesn't show until 768px/md). Below 640px it's
              rendered instead inside the mobile icon row (SiteActionBar's
              `trailing` slot), sized to match the rest of that row. */}
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle-tablet"
            className="hidden h-11 w-11 items-center justify-center rounded-md border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white max-md:sm:inline-flex md:hidden"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        </div>
      </div>

      {isAdminRoute || hideActionBar ? null : mobileActionBarOnly ? (
        <div className="sm:hidden">
          <SiteActionBar />
        </div>
      ) : (
        <SiteActionBar />
      )}
    </header>
    {/* Floating hamburger — used when the regular header chrome is hidden
        on mobile (e.g. on /thelastpost with hide_mobile_chrome). Fixed
        top-right so it sits at the same visual position as the original
        hamburger but doesn't drag the rest of the header with it. */}
    {hideMobileChrome ? (
      <button
        type="button"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="floating-menu-toggle"
        className="fixed right-3 top-3 z-50 inline-flex h-11 w-11 items-center justify-center rounded-md border border-black/15 bg-white text-[var(--brand-ink)] shadow-md transition-colors hover:bg-[#0A0F17] hover:text-white md:hidden"
      >
        {menuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
    ) : null}
    {/* Mobile drawer — rendered OUTSIDE the <header> so hiding the
        header on mobile (hideMobileChrome) doesn't take the drawer down
        with it. Position is fixed/absolute so it doesn't need header
        layout context. */}
    {menuOpen ? (
      <>
        <div
          className="fixed inset-0 z-40 bg-[#0A0F17]/40 backdrop-blur-sm md:hidden"
          style={{ top: hideMobileChrome ? 0 : `${headerH}px` }}
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu-backdrop"
        />
        <nav
          className="fixed left-0 right-0 z-50 overflow-y-auto overscroll-contain border-b border-black/10 shadow-xl backdrop-blur-md md:hidden"
          style={{
            top: hideMobileChrome ? 0 : `${headerH}px`,
            maxHeight: `calc(100dvh - ${hideMobileChrome ? 0 : headerH}px)`,
            // When an image is set: layer it underneath the colour tint
            // (so the opacity slider acts like a tint overlay). Without
            // an image, just use the rgba colour as the background.
            ...(drawerBgImage
              ? {
                  backgroundImage: `linear-gradient(${drawerBg}, ${drawerBg}), url("${drawerBgImage}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }
              : { backgroundColor: drawerBg }),
          }}
          data-testid="mobile-menu-drawer"
        >
          <div className="mx-auto max-w-7xl px-5 py-4 sm:px-8">
            <MobileNavTree
              items={fullNav}
              onLinkClick={trackNavClick}
            />

            <div className="mt-4 flex items-center gap-2 border-t border-black/10 pt-4">
              <Link
                to="/archive#search"
                data-testid="mobile-search-link"
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-black/10 px-4 py-2.5 font-display text-sm font-bold text-[var(--brand-ink)]"
              >
                <Search size={14} strokeWidth={2.5} /> Search
              </Link>
              {user?.role === "admin" ? (
                <Link
                  to="/admin"
                  data-testid="mobile-admin-link"
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-[#0A0F17] px-4 py-2.5 font-display text-sm font-bold text-[var(--brand-ink)]"
                >
                  <LayoutDashboard size={14} strokeWidth={2.5} /> Admin
                </Link>
              ) : (
                <a
                  href={BUY_ME_A_COFFEE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#FFDD00] px-4 py-2.5 font-display text-sm font-bold text-[var(--brand-ink)]"
                  data-testid="mobile-cta"
                >
                  <span aria-hidden="true">🍔</span> Buy LAST a Batch!
                </a>
              )}
            </div>
          </div>
        </nav>
      </>
    ) : null}
    </>
  );
};


/**
 * <DesktopNavDropdown> — desktop-only dropdown for a top-level nav item
 * that has children. Opens on hover (mouse enter/leave with a short
 * close delay so moving from the trigger into the panel doesn't flicker
 * shut) and on click/keyboard (for touch laptops + accessibility).
 * The panel's child-link styling matches the mobile drawer's nested
 * list (left accent border + same font/hover treatment) per the ask
 * to keep the two visually consistent.
 */
function DesktopNavDropdown({ item, kids }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const wrapRef = useRef(null);
  const location = useLocation();

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = () => {
    clearCloseTimer();
    setOpen(true);
  };
  const closeSoon = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  // Close on route change and on unmount.
  useEffect(() => {
    setOpen(false);
    return clearCloseTimer;
  }, [location.pathname]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pillCls =
    "relative flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 font-display text-sm font-semibold transition-colors";
  const pillIdleCls = `${pillCls} text-[#002d75] hover:bg-[#002d75]/10`;
  const pillActiveCls = `${pillCls} bg-[#002d75] text-[#6CABDD]`;
  const leafCls =
    "block rounded-md px-3 py-2 text-sm font-display font-bold tracking-tight transition-colors";

  const triggerInternalPath = getInternalPath(item.to);
  const triggerIsExternal =
    item.target === "blank" || (triggerInternalPath === null && /^https?:\/\//i.test(item.to || ""));

  const renderChild = (c) => {
    const internalPath = getInternalPath(c.to);
    const isExternal =
      c.target === "blank" || (internalPath === null && /^https?:\/\//i.test(c.to || ""));
    if (isExternal) {
      return (
        <a
          key={c.id}
          href={c.to}
          target="_blank"
          rel="noreferrer"
          onClick={() => { trackNavClick(c.id); setOpen(false); }}
          data-testid={`nav-tab-desktop-child-${c.id}`}
          className={`${leafCls} text-[#002d75] hover:bg-[#002d75]/10`}
        >
          {c.label}
        </a>
      );
    }
    return (
      <NavLink
        key={c.id}
        to={internalPath || c.to}
        end={c.to === "/"}
        onClick={() => { trackNavClick(c.id); setOpen(false); }}
        data-testid={`nav-tab-desktop-child-${c.id}`}
        className={({ isActive }) =>
          `${leafCls} ${isActive ? "bg-[#002d75] text-[#6CABDD]" : "text-[#002d75] hover:bg-[#002d75]/10"}`
        }
      >
        {c.label}
      </NavLink>
    );
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      {/* Trigger navigates to the item's own page on click — the dropdown
          panel only opens on hover (via the wrapper's mouse enter/leave
          above), it never intercepts the click. */}
      {triggerIsExternal ? (
        <a
          href={item.to}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackNavClick(item.id)}
          aria-haspopup="true"
          data-testid={`nav-tab-${item.id}`}
          className={pillIdleCls}
        >
          {item.label}
          <ChevronDown size={14} strokeWidth={2.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </a>
      ) : (
        <NavLink
          to={triggerInternalPath || item.to}
          end={item.to === "/"}
          onClick={() => trackNavClick(item.id)}
          aria-haspopup="true"
          data-testid={`nav-tab-${item.id}`}
          className={({ isActive }) => (isActive ? pillActiveCls : pillIdleCls)}
        >
          {item.label}
          <ChevronDown size={14} strokeWidth={2.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </NavLink>
      )}
      {open ? (
        <div
          className="absolute left-0 top-full z-[60] mt-2 min-w-[190px] rounded-xl border border-black/10 bg-white p-2 shadow-xl"
          data-testid={`nav-dropdown-panel-${item.id}`}
        >
          <ul className="space-y-1 border-l-2 border-[#6CABDD]/40 pl-3">
            {kids.map((c) => (
              <li key={c.id}>{renderChild(c)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * <MobileNavTree> — 1-level-deep expandable menu used inside the mobile
 * drawer. Top-level rows behave like the previous flat menu (tap to
 * navigate); rows with children get an extra chevron on the right that
 * toggles a nested list of the children. Tapping the chevron does NOT
 * navigate. Tapping the top row's label DOES navigate.
 */
function MobileNavTree({ items, onLinkClick }) {
  const [openIds, setOpenIds] = useState(() => new Set());
  const { tops, childrenOf } = buildNavTree(items);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderLeaf = (t, isChild) => {
    const internalPath = getInternalPath(t.to);
    const isExternal =
      t.target === "blank" || (internalPath === null && /^https?:\/\//i.test(t.to || ""));
    const cls = `block rounded-md ${isChild ? "px-3 py-2 text-sm" : "px-4 py-3 text-base"} font-display font-bold tracking-tight transition-colors`;
    if (isExternal) {
      return (
        <a
          href={t.to}
          target="_blank"
          rel="noreferrer"
          onClick={() => onLinkClick?.(t.id)}
          data-testid={`nav-tab-mobile-${t.id}`}
          className={`${cls} text-[#002d75] hover:bg-[#002d75]/10`}
        >
          {t.label}
        </a>
      );
    }
    return (
      <NavLink
        to={internalPath || t.to}
        end={t.to === "/"}
        onClick={() => onLinkClick?.(t.id)}
        data-testid={`nav-tab-mobile-${t.id}`}
        className={({ isActive }) =>
          `${cls} ${
            isActive
              ? "bg-[#002d75] text-[#6CABDD]"
              : "text-[#002d75] hover:bg-[#002d75]/10"
          }`
        }
      >
        {t.label}
      </NavLink>
    );
  };

  return (
    <ul className="flex flex-col gap-1" data-testid="mobile-nav-tree">
      {tops.map((t) => {
        const kids = childrenOf[t.id] || [];
        const hasChildren = kids.length > 0;
        const isOpen = openIds.has(t.id);
        return (
          <li key={t.id}>
            <div className={`flex items-stretch ${hasChildren ? "gap-1" : ""}`}>
              <div className="min-w-0 flex-1">{renderLeaf(t, false)}</div>
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => toggle(t.id)}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? `Collapse ${t.label}` : `Expand ${t.label}`}
                  data-testid={`nav-tree-toggle-${t.id}`}
                  className="flex w-11 shrink-0 items-center justify-center rounded-md text-[#002d75] hover:bg-[#002d75]/10"
                >
                  <ChevronDown
                    size={18}
                    strokeWidth={2.5}
                    className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
              ) : null}
            </div>
            {hasChildren && isOpen ? (
              <ul
                className="mt-1 space-y-1 border-l-2 border-[#6CABDD]/40 pl-3"
                data-testid={`nav-tree-children-${t.id}`}
              >
                {kids.map((c) => (
                  <li key={c.id}>{renderLeaf(c, true)}</li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
